import { useId, useState, type FormEvent } from "react";
import type { ApiError } from "./api";

/** The server's bound, repeated only to count against. `teams.team_name` is the rule. */
export const TEAM_NAME_MAX = 40;

/**
 * How long the name the server will STORE is: whitespace collapsed, and counted in characters.
 *
 * ⚠️ NOT `value.length`. That counts UTF-16 units, so one emoji reads as two and a name the
 * server accepts would show as over the limit.
 */
export const nameLength = (raw: string) =>
  [...raw.split(/\s+/).filter(Boolean).join(" ")].length;

/**
 * Name a team, or clear its name. A student uses it on their own team's card; a teacher uses it
 * on the review card. One definition, so both are told the same rule.
 *
 * 🔴 THE SERVER'S SENTENCE IS THE RULE. The counter is an affordance, and Save is never disabled
 * for length: a refused name comes back with the reason, and a client that blocks the click
 * hides it.
 * ⚠️ ESCAPE IS HEARD ON THE FORM, NOT THE DOCUMENT, so a closed editor listens to nothing.
 */
export function TeamNameForm({ number, name, hint, onSave, onClose }: {
  number: number; name: string;
  /** Who sees the name, in words for whoever is typing it. */
  hint: string;
  /** Rejects with the server's refusal, which is shown here and keeps the form open. */
  onSave: (name: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState(name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const n = nameLength(draft);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { await onSave(draft); onClose(); }
    catch (x) { setErr(x as ApiError); }
    finally { setBusy(false); }
  };

  return (
    <form className="tname-form" onSubmit={(e) => void save(e)}
          onKeyDown={(e) => { if (e.key === "Escape" && !busy) onClose(); }}>
      <label className="q-label" htmlFor={`${id}-name`}>Team name</label>
      {/* ⚠️ 200 IS THE ROUTE'S EDGE BOUND, NOT THE RULE. It only stops a paste reaching a 422
          that names a pydantic code; anything up to it gets the server's own sentence. */}
      <input id={`${id}-name`} className="input wide" value={draft} maxLength={200}
             placeholder={`Team ${number}`} autoFocus disabled={busy}
             aria-describedby={`${id}-hint`} onChange={(e) => setDraft(e.target.value)} />
      <p className="q-hint" id={`${id}-hint`}>
        {hint} One line, at most {TEAM_NAME_MAX} characters. Leave it empty to go back to
        “Team {number}”.{" "}
        <span className={n > TEAM_NAME_MAX ? "tname-count over" : "tname-count"}>
          {n}/{TEAM_NAME_MAX}
        </span>
      </p>
      {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
      <div className="row gap">
        <button className="btn primary sm" type="submit" disabled={busy}>Save name</button>
        <button className="btn ghost sm" type="button" disabled={busy} onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
